import moment from 'moment'
import { pluralize } from 'utils/misc'
import { DATE_FORMAT_MAP } from 'utils/constants'
import { parseVisibilityTime } from 'utils/time'
import { fromApiSchedule } from '../adapters/fromApiSchedule'
import { isV3ScheduleEntry } from '../adapters/fromLegacyV2Schedule'
import {
  ANCHOR_TYPE,
  AVAILABILITY_MODE,
  AVAILABILITY_MODE_LABELS,
  END_RULE_TYPE,
  END_RULES_COMBINE,
  FIXED_DATE_MODE,
  REPEAT_MODE,
  REPEAT_MODE_LABELS,
} from '../constants'
import { summarizeAnchorShort, summarizeCombinedShort } from './anchorSummary'
import { namesForAnchor } from './entityNames'
import { buildScheduleLabel } from './plainLanguage'
import { summarizeRepeat } from './repeatSummary'
import { shouldShowDeployTimeOfDay } from './deployTimeOfDayDisplay'
import { formatRelativeToVisitEndRule } from './endRuleSummary'

export { isV3ScheduleEntry }

export const LEGACY_SCHEDULE_FIELD_LABELS = {
  start: 'Start',
  expire: 'Expire',
  frequency: 'Frequency',
  visibility: 'Visibility',
  sendTo: 'Send to',
}

export const ENGINE_SCHEDULE_FIELD_LABELS = {
  start: 'Start',
  expire: 'End',
  frequency: 'Repeat',
  visibility: 'Availability',
  sendTo: 'Audience',
}

export const getSchedulePreviewFieldLabels = schedule =>
  isV3ScheduleEntry(schedule) ? ENGINE_SCHEDULE_FIELD_LABELS : LEGACY_SCHEDULE_FIELD_LABELS

const toUiSchedule = apiEntry => fromApiSchedule(apiEntry)

const capitalize = text => {
  if (!text) return text
  return text.charAt(0).toUpperCase() + text.slice(1)
}

const formatClockLabel = timeStr => {
  const parsed = parseVisibilityTime(timeStr)
  if (!parsed) return null
  return parsed.format('h:mm A')
}

const formatExactDate = value => {
  if (!value) return ''
  const parsed = moment.utc(value, [DATE_FORMAT_MAP.datePicker, moment.ISO_8601], true)
  if (!parsed.isValid()) {
    const fallback = moment.utc(value)
    return fallback.isValid() ? fallback.format(DATE_FORMAT_MAP.main) : String(value)
  }
  return parsed.format(DATE_FORMAT_MAP.main)
}

export const buildEntityOptionsFromNameContext = (nameContext = {}) => {
  const { instrumentNameMap = {}, studyVisitSchedule = [], visitTemplates = [] } = nameContext
  const visits = []
  const arms = []
  const cycles = []
  const phases = []

  ;(studyVisitSchedule || []).forEach(arm => {
    if (arm?.id) arms.push({ id: String(arm.id), name: arm.name || String(arm.id) })
    ;(arm?.phases || []).forEach(phase => {
      if (phase?.id) phases.push({ id: String(phase.id), name: phase.name || String(phase.id) })
      ;(phase?.cycles || []).forEach(cycle => {
        if (cycle?.id) cycles.push({ id: String(cycle.id), name: cycle.name || String(cycle.id) })
        ;(cycle?.visits || []).forEach(visit => {
          if (!visit?.id) return
          visits.push({
            id: String(visit.id),
            name: visit.name || String(visit.id),
            armId: arm?.id ? String(arm.id) : '',
          })
        })
      })
    })
  })

  ;(visitTemplates || []).forEach(visit => {
    if (!visit?.id) return
    const id = String(visit.id)
    if (visits.some(item => item.id === id)) return
    visits.push({ id, name: visit.name || visit.display_name || id })
  })

  const instruments = Object.entries(instrumentNameMap || {}).map(([id, name]) => ({
    id: String(id),
    name,
  }))

  return { visits, arms, cycles, phases, instruments }
}

const formatFixedDateAnchor = anchor => {
  const mode = anchor?.fixedDate?.mode
  const value = anchor?.fixedDate?.value
  if (mode === FIXED_DATE_MODE.dayOfYear && value) return `Day ${value} of year`
  if (mode === FIXED_DATE_MODE.weekOfYear && value) return `Week ${value} of year`
  if (value) return formatExactDate(value)
  return 'Exact Date'
}

const formatEngineStartHeadline = (ui, nameContext) => {
  const entityOptions = buildEntityOptionsFromNameContext(nameContext)
  const nameFor = anchor => namesForAnchor(anchor, entityOptions)
  if (ui.combineAnchors) return summarizeCombinedShort(ui, nameFor)

  const first = ui.anchors?.[0]
  if (!first) return ''
  if (first.type === ANCHOR_TYPE.fixedCalendarDate) return formatFixedDateAnchor(first)
  return summarizeAnchorShort(first, nameFor(first))
}

export const getEngineScheduleStartTypeLabel = schedule => {
  const ui = toUiSchedule(schedule)
  const type = ui.anchors?.[0]?.type
  if (ui.combineAnchors) return 'Combined Anchor'
  if (type === ANCHOR_TYPE.participantLogin) return 'Relative to First Login'
  if (type === ANCHOR_TYPE.icfSignatureDate) return 'Relative to ICF Signature'
  if (type === ANCHOR_TYPE.fixedCalendarDate) return 'Exact Date'
  if (type === ANCHOR_TYPE.arm) return 'Relative to Arm'
  if (type === ANCHOR_TYPE.phase) return 'Relative to Phase'
  if (type === ANCHOR_TYPE.track) return 'Relative to Track'
  if (type === ANCHOR_TYPE.chapter) return 'Relative to Chapter'
  return buildScheduleLabel(ui)
}

export const getEngineScheduleStartValue = (schedule, nameContext = {}) => {
  const ui = toUiSchedule(schedule)
  let headline = formatEngineStartHeadline(ui, nameContext)
  if (shouldShowDeployTimeOfDay(ui)) {
    const timeLabel = formatClockLabel(ui.timeOfDay || schedule?.deploy?.time_of_day)
    if (timeLabel && headline) headline = `${headline} (${timeLabel})`
    else if (timeLabel) headline = timeLabel
  }
  return headline || getEngineScheduleStartTypeLabel(schedule)
}

const summarizeEngineEndRules = (schedule, ui) => {
  const rules = ui?.endRules?.rules || schedule?.end_rules?.rules || []
  if (!rules.length) return null
  const bits = rules
    .map(rule => {
      const type = rule?.type
      if (type === END_RULE_TYPE.afterNOccurrences || type === 'after_n_occurrences') {
        const count = rule.count ?? rule.recurrences
        if (count != null) return `After ${count} occurrences`
      }
      if (type === END_RULE_TYPE.relativeToVisit || type === 'relative_to_visit') {
        return formatRelativeToVisitEndRule(rule)
      }
      return null
    })
    .filter(Boolean)
  if (!bits.length) return null
  if (bits.length === 1) return bits[0]
  const combine = ui?.endRules?.combine || schedule?.end_rules?.combine
  const joiner =
    combine === END_RULES_COMBINE.latestOf || combine === 'latest_of'
      ? 'latest of'
      : combine === END_RULES_COMBINE.earliestOf || combine === 'earliest_of'
        ? 'earliest of'
        : ''
  return joiner ? `${joiner} ${bits.join(' or ')}` : bits.join(' or ')
}

export const getEngineScheduleExpireValue = schedule => {
  const ui = toUiSchedule(schedule)
  const fromRules = summarizeEngineEndRules(schedule, ui)
  if (fromRules) return fromRules
  const recurrences = schedule?.recurring?.recurrences
  if (recurrences) return `After ${recurrences} occurrences`
  return 'Never'
}

export const getEngineScheduleRepeatValue = schedule => {
  const ui = toUiSchedule(schedule)
  const mode = ui.repeat?.mode
  if (!mode || mode === REPEAT_MODE.doesNotRepeat || mode === 'does_not_repeat') {
    return REPEAT_MODE_LABELS[REPEAT_MODE.doesNotRepeat] || "Doesn't repeat"
  }
  if (mode === REPEAT_MODE.everyNDays || mode === 'every_n_days') {
    const interval = ui.repeat.intervalDays ?? schedule?.repeat?.interval_days ?? schedule?.recurring?.interval ?? 1
    return `Every ${pluralize(Number(interval) || 1, 'day', 'days')}`
  }
  if (mode === 'persistent' || schedule?.recurring?.scheme === 'persistent') {
    return 'Persistent (daily)'
  }
  if (REPEAT_MODE_LABELS[mode]) return REPEAT_MODE_LABELS[mode]
  const summary = summarizeRepeat(ui.repeat)
  return summary ? capitalize(summary) : REPEAT_MODE_LABELS[REPEAT_MODE.doesNotRepeat]
}

export const getEngineScheduleVisibilityValue = (schedule, metadata = null) => {
  const ui = toUiSchedule(schedule)
  const availability = ui.availability || {}
  const daily = availability.dailyWindow || schedule?.availability?.daily_window
  if (daily?.enabled && (daily.from || daily.until)) {
    const from = formatClockLabel(daily.from)
    const until = formatClockLabel(daily.until)
    if (from && until) return `${from} – ${until}`
    return 'At Specific Times'
  }
  if (availability.mode === AVAILABILITY_MODE.fixedDuration) {
    const value = availability.openFor?.value
    const unit = availability.openFor?.unit || 'hours'
    if (value != null) return `Open for ${value} ${unit}`
  }
  if (availability.mode && availability.mode !== AVAILABILITY_MODE.fixedDuration) {
    return AVAILABILITY_MODE_LABELS[availability.mode] || 'All day'
  }
  const visibility = schedule?.visibility ?? metadata?.visibility
  if (visibility === 'specific_time') return 'At Specific Times'
  return 'All day'
}

export const hasEngineInstrumentAnchor = schedule =>
  (schedule?.deploy?.anchors || []).some(anchor => anchor?.type === 'instrument')
